import React, { useContext, useEffect, useState } from "react";
import { APIContext } from "../Contexts/APIContext"
import ScorecardDropdownViewModel from "../Contexts/ViewModels/ScorecardDropdownViewModel";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import ScorecardTemplate from "./Scorecards/ScorecardTemplate";
import ExistingScorecardViewModel from "./ViewModels/ExistingSorecardViewModel";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import ExistingScorecardTable from "./Scorecards/ExistingScorecardTable";
import { MsalContext } from "../Contexts/MsalContext";
import ScorecardProvider from "../Contexts/ScorecardContext";
import Alert from "@mui/material/Alert";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

interface IProps {
    callRefId: string;
    agentView?: boolean;
    // id of scorecard created when rescoring a challenged scorecard
    scorecardRescoreId?: number;
    isUsersCall?: boolean;
    isTeamLeader: boolean;
    toggleTimerActive?: (active: boolean) => void;
    mainView?: boolean;
}

export default function MonitoringScoreCard(props: IProps) {
    const { get, loading, isAdmin, isQA, isTeamLeader } = useContext(APIContext);
    const { msalFetch } = useContext(MsalContext);
    const [callOptions, setCallOptions] = useState<ScorecardDropdownViewModel[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
    const [isComplete, setIsComplete] = useState<boolean>(false);
    const [aiCard, setAiCard] = useState<boolean>(false);
    const [monitorDurationId, setMonitorDurationId] = useState<number>(0);
    const interval = React.useRef<ReturnType<typeof setInterval> | null>(null);
    const [scorecards, setScorecards] = useState<ExistingScorecardViewModel[]>([]);
    const [loadedScorecardId, setLoadedScorecardId] = useState<number | null>(null);
    const [pdf, setpdf] = useState<string>('0');
    const [hasLoaded, setHasLoaded] = useState<boolean>(false);
    const [aiScorecardFailMessage, setAiScorecardFailMessage] = useState<string | null>(null);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        initialise(true);
        if (props.scorecardRescoreId) {
            loadScorecard(props.scorecardRescoreId);
        }
    }, []);

    useEffect(() => {
        if (loadedScorecardId) {
            startMonitorDuration()
        }
    }, [loadedScorecardId])

    useEffect(() => {
        if (monitorDurationId) {
            interval.current = startInterval(monitorDurationId);
            if (props.toggleTimerActive) props.toggleTimerActive(true);
        }
        else {
            clearTimeout(interval.current!);
            updateMonitorDuration(monitorDurationId);
            if (props.toggleTimerActive) props.toggleTimerActive(false);
        }
        return () => {
            clearTimeout(interval.current!);
            updateMonitorDuration(monitorDurationId);
            if (props.toggleTimerActive) props.toggleTimerActive(false);
        }
    }, [monitorDurationId]);

    const startInterval = (durationId: number) => {
        console.log("start interval");
        const updateDuration = setInterval(() => {
            updateMonitorDuration(durationId);
        }, 30000);
        return updateDuration;
    };

    const updateMonitorDuration = (durationId: number) => {
        if (!isComplete && durationId) {
            get('MonitoringScorecard', `UpdateMonitoringDuration?durationId=${durationId}`);
        }
    };

    const startMonitorDuration = () => {
        get('MonitoringScorecard', `StartMonitoringDuration?callRefId=${props.callRefId}&completeId=${loadedScorecardId}`, (data) => {
            if (typeof data === "boolean") {
                setIsComplete(data);
            }
            else {
                setMonitorDurationId(data);
            }
        });
    };

    const getScorecardOptions = () => {
        get('MonitoringScorecard', `GetScorcardOptions?callid=${props.callRefId}`, (data) => {
            setCallOptions(data)
        });
    }

    const initialise = (firstLoad: boolean) => {
        if (!props.scorecardRescoreId) {
            getScorecardOptions();
        }
        checkAiScorecardIsProcessed();
        checkExistingScorecards(firstLoad);
    };

    const selectTemplate = (e) => {
        setSelectedTemplate(e.target.value);
    }

    const checkAiScorecardIsProcessed = () => {
        get(
            'MonitoringScorecard',
            `CheckAIScorecardIsProcessed?callRefId=${props.callRefId}`,
            (data) => {
                if (data.IsProcessed) {
                    setAiScorecardFailMessage(null);
                }
                else {
                    setAiScorecardFailMessage(data.message);
                }
            })
    }

    const checkExistingScorecards = (firstLoad: boolean) => {
        get('MonitoringScorecard', `CheckForExistingScorecards?callRefId=${props.callRefId}`, (data) => {
            if (data) {
                // We only want to redirect on mount to stop loops
                setScorecards(data);

                if (firstLoad === true) {
                    const humanScorecard = data.find(f => f.type === 'Monitoring' && f.complete);
                    if (humanScorecard) {
                        getPdf(humanScorecard.completeId);
                        return;
                    }

                    let aiScorecard = data.find(w => w.type === "AI-Monitoring");

                    if (!aiScorecard) {
                        aiScorecard = data.find(w => w.type === "AI-Monitoring 2")
                    }

                    if (aiScorecard) {
                        getPdf(aiScorecard.completeId);
                        setAiCard(true);
                        return;
                    }
                }

                setHasLoaded(true);
            }
        });
    };

    const loadScorecard = (completeId: number) => {
        setLoadedScorecardId(completeId);
    }

    const getPdf = (completeId: number) => {

        msalFetch(`api/PDFGenerator/CreateScorecardPDF?completeId=${completeId}`)
            .then(response => response.blob())
            .then(data => {
                if (data.type === "application/problem+json") {
                    console.log('error', 'An internal server error occured');
                    return;
                }
                setHasLoaded(true);
                var url = window.URL.createObjectURL(data);
                setpdf(url);
                loadScorecard(completeId);
            })
            .catch(e => {
                setHasLoaded(true);
                console.log(e);
            });
    }

    const createNewScorecard = () => {
        if (!loading) {
            const template = callOptions[callOptions.findIndex(f => f.id === selectedTemplate)];
            const id = template.id.split(" ")[0];
            get('MonitoringScorecard', `CreateScorecard?callRefId=${props.callRefId}&templateId=${id}&levelling=${template.levelling}&calibration=${template.calibration}&qaCopy=${template.qaCopy}`, (id: number) => {
                getScorecardOptions();
                setLoadedScorecardId(id);
                checkExistingScorecards(false);
            });
        }
    }

    const resetPDF = () => {
        setpdf('0')
    }

    const backToMenu = () => {
        resetPDF();
        setLoadedScorecardId(null);
        setMonitorDurationId(0);
        initialise(false);
        setAiCard(false);
    }

    return (
        ((loadedScorecardId || props.scorecardRescoreId)) ?
            <ScorecardProvider>
                <ScorecardTemplate
                    callRefId={props.callRefId}
                    scorecardId={props.scorecardRescoreId! ?? loadedScorecardId}
                    backToMenu={backToMenu}
                    scorecardBuilderView={false}
                    rescoreView={!!props.scorecardRescoreId}
                    pdf={pdf}
                    scorecardCount={scorecards?.length ?? 0}
                    mainView={props.mainView}
                    toggleTimerActive={props.toggleTimerActive}
                />
            </ScorecardProvider>
            :
            <>
                {scorecards.length === 0 && props.agentView ? (
                    <Grid container spacing={1} alignItems="center">
                        {!loading &&
                            <Grid size={12} alignItems="center">
                                <Typography alignSelf="center" variant='h6' sx={{ paddingLeft: '8px', paddingTop: '8px' }}>No Feedback Form Found</Typography>
                            </Grid>
                        }
                    </Grid>
                ) : (
                    <Box padding={isSmallScreen ? 0.5 : 1} sx={{ overflowY: 'auto', height: 'inherit' }}>
                        {
                            props.isUsersCall === false ? (
                                <Grid container justifyContent="center" alignItems="center">
                                    <Grid sx={{ size: { xs: 12 } }}>
                                        <Typography variant="h5" align={isSmallScreen ? "center" : undefined}>Feedback is for another agent</Typography>
                                    </Grid>
                                </Grid>
                            ) : (
                                <>
                                    {aiScorecardFailMessage &&
                                        <Alert severity="error">{aiScorecardFailMessage}</Alert>
                                    }
                                    <Paper sx={{ width: "100%", overflowX: "auto", padding: isSmallScreen ? 1 : 2 }}>
                                        <Grid container spacing={1} alignItems="center">
                                            {scorecards.length > 0 && pdf === '0' && !aiCard && hasLoaded &&
                                                <ExistingScorecardTable
                                                    scorecards={scorecards}
                                                    loadScorecard={loadScorecard}
                                                    scorecardRescoreId={props.scorecardRescoreId}
                                                    checkExistingScorecards={checkExistingScorecards}
                                                    getScorecardOptions={getScorecardOptions}
                                                    agentView={props.agentView}
                                                    viewPDF={getPdf}
                                                />
                                            }
                                            {
                                                !props.scorecardRescoreId
                                                && !props.agentView
                                                && !aiCard
                                                && hasLoaded
                                                && (isAdmin || isQA || isTeamLeader) &&
                                                <>
                                                    {callOptions.length === 0 ?
                                                        <Grid sx={{ size: { xs: 12 } }}>
                                                            <Typography variant="h5" align={isSmallScreen ? "center" : undefined}>No scorecard templates available for this call</Typography>
                                                        </Grid>
                                                        :
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                width: '100%',
                                                                gap: 1, // optional: space between the two halves
                                                            }}
                                                        >
                                                            <Box sx={{ flex: 0.7 }}>
                                                                <Select
                                                                    disabled={callOptions.length === 0}
                                                                    value={selectedTemplate}
                                                                    onChange={selectTemplate}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    margin="dense"
                                                                    fullWidth
                                                                >
                                                                    {callOptions.map(m => (
                                                                        <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </Box>
                                                            <Box sx={{ flex: 0.3 }}>
                                                                <Button
                                                                    disabled={selectedTemplate == null}
                                                                    fullWidth
                                                                    onClick={() => createNewScorecard()}
                                                                    variant="contained"
                                                                    sx={{
                                                                        minHeight: '40px'
                                                                    }}
                                                                >
                                                                    Create New Scorecard
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    }
                                                </>
                                            }
                                            {(props.agentView) &&
                                                <>
                                                    {pdf !== '0' &&
                                                        <Grid
                                                            sx={{
                                                                mb: isSmallScreen ? 1 : 0,
                                                                size: { xs: 12, sm: 2 }
                                                            }}>
                                                            <Button
                                                                fullWidth
                                                                onClick={() => resetPDF()}
                                                                variant="outlined">
                                                                Back
                                                            </Button>
                                                        </Grid>
                                                    }
                                                    <Grid size={12} sx={{
                                                        height: isSmallScreen ? '50vh' : '690px',
                                                        overflow: 'auto'
                                                    }}>
                                                        {pdf != '0' &&
                                                            <Box sx={{
                                                                width: "100%",
                                                                height: "100%",
                                                                overflowX: "auto"
                                                            }}>
                                                                <object
                                                                    width="100%"
                                                                    height="100%"
                                                                    data={pdf + "#toolbar=0"}
                                                                    type="application/pdf"
                                                                    style={{ minWidth: 300 }}
                                                                />
                                                            </Box>
                                                        }
                                                    </Grid>
                                                </>
                                            }
                                        </Grid >
                                    </Paper>
                                </>
                            )
                        }
                    </Box>
                )}
            </>
    );
}